/**
 * MultiComboboxField — searchable multi-select for long option lists
 * (e.g. additional citizenships over the full country list).
 *
 * Selected values render as removable chips showing their LABEL (never the raw
 * value/ISO code); a type-to-filter input adds more. Stores string[] of the
 * selected option values, matching CheckboxGroup's contract so the stored data
 * shape is unchanged. Hand-rolled to mirror ComboboxField (no runtime dep).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import type { RegisterOptions } from 'react-hook-form';
import { X } from 'lucide-react';
import { FieldTooltip } from '../FieldTooltip';
import type { FieldTooltipEntry } from '../../lib/fieldTooltips';
import { matchesQuery } from '../../lib/comboboxFilter';

interface Option {
  value: string;
  label: string;
}

interface Props {
  name: string;
  label: string;
  options: Option[];
  hint?: string;
  required?: boolean;
  tooltip?: FieldTooltipEntry;
  placeholder?: string;
  rules?: RegisterOptions;
}

export function MultiComboboxField({
  name,
  label,
  options,
  hint,
  required,
  tooltip,
  placeholder = 'Search and add…',
  rules,
}: Props) {
  const { control } = useFormContext();
  // RC-4: specific "<label> is required" message, overridable by an explicit
  // rules.required (buildValidationRules) that matches the submit-gate summary.
  const { field } = useController({
    name,
    control,
    rules: { required: required ? `${label} is required` : false, ...rules },
  });

  const reactId = useId();
  const inputId = `${reactId}-multicombobox`;
  const listId = `${reactId}-listbox`;

  const selected: string[] = useMemo(
    () => (Array.isArray(field.value) ? field.value : []),
    [field.value],
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOptions = useMemo(
    () => selected.map((v) => options.find((o) => o.value === v) ?? { value: v, label: v }),
    [selected, options],
  );

  const filtered = useMemo(
    () =>
      options
        .filter((o) => !selected.includes(o.value))
        .filter((o) => matchesQuery(o.label, query)),
    [options, selected, query],
  );

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function add(opt: Option) {
    if (!selected.includes(opt.value)) field.onChange([...selected, opt.value]);
    setQuery('');
    setActiveIndex(0);
  }

  function remove(value: string) {
    field.onChange(selected.filter((v) => v !== value));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        add(filtered[activeIndex]);
      }
    } else if (e.key === 'Backspace' && query === '' && selected.length) {
      remove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="form-label" htmlFor={inputId}>
        {label}
        {required ? (
          <>
            <span aria-hidden="true" className="text-[#FB8E28] ml-0.5">*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : (
          <span className="form-label-optional">(optional)</span>
        )}
        {tooltip && (
          <FieldTooltip description={tooltip.description} examples={tooltip.examples} ariaLabel={`Help for ${label}`} />
        )}
      </label>

      {selectedOptions.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mb-1.5" aria-label={`${label} selected`}>
          {selectedOptions.map((o) => (
            <li key={o.value} className="inline-flex items-center gap-1 rounded-full bg-[#EBF7FD] text-[#0B2341] text-sm pl-3 pr-1.5 py-1">
              {o.label}
              <button
                type="button"
                onClick={() => remove(o.value)}
                aria-label={`Remove ${o.label}`}
                className="rounded-full hover:bg-white/60 p-0.5"
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        id={inputId}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        className="form-input"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
          setActiveIndex(0);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={field.onBlur}
      />

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-300 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-400">
              {query ? 'No matches' : 'All added'}
            </li>
          ) : (
            filtered.slice(0, 100).map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(opt);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  i === activeIndex ? 'bg-[#EBF7FD] text-[#0B2341]' : 'text-slate-700'
                }`}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}

      {hint && <p className="form-hint">{hint}</p>}
    </div>
  );
}
